import * as React from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

/**
 * Standardized Button Component
 * 
 * Design System Specifications:
 * - Primary: Blue background (#0084c7) for main CTAs
 * - Secondary: White background with border for secondary actions
 * - Ghost: Transparent background for tertiary actions
 * - Danger: Red tones for destructive actions
 * - Success: Green tones for confirmations
 * - Outline: Border only variant
 * 
 * Usage:
 * ```tsx
 * <Button variant="primary" size="md">Save</Button>
 * <Button variant="secondary" icon={<Plus />}>Add Item</Button>
 * <Button variant="danger" isLoading>Delete</Button>
 * ```
 */
export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center gap-2 rounded-[10px] font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-[#0084c7] text-white hover:bg-[#006ba1] focus:ring-[#0084c7]/50',
    secondary: 'border border-green-700 text-[#1c1c1c] hover:bg-[#f5f5f5] focus:ring-[#0084c7]/30',
    ghost: 'bg-transparent text-[#1c1c1c] hover:bg-[#f5f5f5] focus:ring-[#0084c7]/30',
    danger: 'bg-[#dc2626] text-white hover:bg-[#b91c1c] focus:ring-[#dc2626]/50',
    success: 'bg-[#16a34a] text-white hover:bg-[#15803d] focus:ring-[#16a34a]/50',
    outline: 'bg-transparent border border-[#0084c7] text-[#0084c7] hover:bg-[#0084c7]/10 focus:ring-[#0084c7]/30',
  };
  
  const sizes = {
    sm: 'h-[36px] px-3 text-[13px]',
    md: 'h-[44px] px-4 text-[14px]',
    lg: 'h-[48px] px-6 text-[16px]',
  };
  
  const widthClass = fullWidth ? 'w-full' : '';
  
  const combinedClassName = `${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`.trim();
  
  return (
    <button
      className={combinedClassName}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="size-4 animate-spin" />}
      {!isLoading && icon && iconPosition === 'left' && <span className="shrink-0">{icon}</span>}
      {children}
      {!isLoading && icon && iconPosition === 'right' && <span className="shrink-0">{icon}</span>}
    </button>
  );
}
